import { useRef, useCallback } from "react";

import { Recorder } from "@/components/embedded-avatar/audio/recorder";

const BUFFER_SIZE = 4800;

type Parameters = {
  onAudioRecorded: (base64: string) => void;
};

export default function useAudioRecorder({ onAudioRecorded }: Parameters) {
  const audioRecorder = useRef<Recorder>();
  const buffer = useRef<Uint8Array>(new Uint8Array());

  const appendToBuffer = useCallback((newData: Uint8Array) => {
    const newBuffer = new Uint8Array(buffer.current.length + newData.length);
    newBuffer.set(buffer.current);
    newBuffer.set(newData, buffer.current.length);
    buffer.current = newBuffer;
  }, []);

  const handleAudioData = useCallback((data: Iterable<number>) => {
    const uint8Array = new Uint8Array(data);
    appendToBuffer(uint8Array);

    if (buffer.current.length >= BUFFER_SIZE) {
      const toSend = new Uint8Array(buffer.current.slice(0, BUFFER_SIZE));
      buffer.current = new Uint8Array(buffer.current.slice(BUFFER_SIZE));

      const regularArray = String.fromCharCode(...toSend);
      const base64 = btoa(regularArray);

      onAudioRecorded(base64);
    }
  }, [onAudioRecorded, appendToBuffer]);

  const start = useCallback(async () => {
    if (!audioRecorder.current) {
      audioRecorder.current = new Recorder(handleAudioData);
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    try {
      audioRecorder.current.start(stream);
    } catch (error) {
      alert("Microphone access denied");
      console.error(error);
    }
  }, [handleAudioData]);

  const stop = useCallback(async () => {
    await audioRecorder.current?.stop();
  }, []);

  return { start, stop };
}
